import React, { createContext, useContext, useState, ReactNode, useCallback } from "react";
import apiInstance from "../services/apiInstance";

// Define types for User data
export interface User {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    status: string;
    companyId: number;
    createdAt: string;
    updatedAt: string;
    company?: {
        name: string;
        logo?: string;
    };
}

interface UserPagination {
    users: User[];
    total: number;
    pages: number;
    currentPage: number;
}

// Define the context state
interface UserContextState {
    users: User[];
    currentUser: User | null;
    loading: boolean;
    error: string | null;
    pagination: {
        total: number;
        pages: number;
        currentPage: number;
    };
    fetchUsers: (page?: number, limit?: number) => Promise<void>;
    fetchUserById: (id: number) => Promise<User | null>;
    addUser: (userData: Partial<User>) => Promise<User | null>;
    editUser: (id: number, userData: Partial<User>) => Promise<User | null>;
    deleteUser: (id: number) => Promise<boolean>;
    inviteUser: (email: string, role: string, companyId: number) => Promise<any>;
    clearError: () => void;
}

// Create the context
const UserContext = createContext<UserContextState | undefined>(undefined);

// Provider component
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        total: 0,
        pages: 0,
        currentPage: 1,
    });

    const clearError = useCallback(() => {
        setError(null);
    }, []);
    // Fetch all users (paginated)
    const fetchUsers = useCallback(async (page = 1, limit = 10) => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiInstance.get(`/users?page=${page}&limit=${limit}`);
            const data = response.data.data as UserPagination;

            setUsers(data.users);
            setPagination({
                total: data.total,
                pages: data.pages,
                currentPage: data.currentPage,
            });
        } catch (error: any) {
            setError(error.response?.data?.message || "Failed to fetch users");
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    }, []);
    // Fetch a single user by ID
    const fetchUserById = useCallback(async (id: number) => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiInstance.get(`/users/${id}`);
            const user = response.data.data as User;
            setCurrentUser(user);
            return user;
        } catch (error: any) {
            setError(error.response?.data?.message || "Failed to fetch user details");
            console.error("Error fetching user:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);
    // Add a new user
    const addUser = useCallback(async (userData: Partial<User>) => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiInstance.post("/users", userData);
            const newUser = response.data.data as User;
            setUsers(prevUsers => [...prevUsers, newUser]);
            return newUser;
        } catch (error: any) {
            setError(error.response?.data?.message || "Failed to create user");
            console.error("Error creating user:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);
    // Edit an existing user
    const editUser = useCallback(async (id: number, userData: Partial<User>) => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiInstance.put(`/users/${id}`, userData);
            const updatedUser = response.data.data as User;

            setUsers(prevUsers =>
                prevUsers.map(user => (user.id === id ? updatedUser : user))
            );

            if (currentUser && currentUser.id === id) {
                setCurrentUser(updatedUser);
            }

            return updatedUser;
        } catch (error: any) {
            setError(error.response?.data?.message || "Failed to update user");
            console.error("Error updating user:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }, [currentUser]);
    // Delete a user
    const deleteUser = useCallback(async (id: number) => {
        setLoading(true);
        setError(null);

        try {
            await apiInstance.delete(`/users/${id}`);
            setUsers(prevUsers => prevUsers.filter(user => user.id !== id));

            if (currentUser && currentUser.id === id) {
                setCurrentUser(null);
            }

            return true;
        } catch (error: any) {
            setError(error.response?.data?.message || "Failed to delete user");
            console.error("Error deleting user:", error);
            return false;
        } finally {
            setLoading(false);
        }
    }, [currentUser]);
    // Invite a new user
    const inviteUser = useCallback(async (email: string, role: string, companyId: number) => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiInstance.post("/users/invite", {
                email,
                role,
                companyId
            });

            // Refresh the users list after inviting a new user
            await fetchUsers(pagination.currentPage);

            return response.data.data;
        } catch (error: any) {
            setError(error.response?.data?.message || "Failed to invite user");
            console.error("Error inviting user:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }, [fetchUsers, pagination.currentPage]);

    const value = {
        users,
        currentUser,
        loading,
        error,
        pagination,
        fetchUsers,
        fetchUserById,
        addUser,
        editUser,
        deleteUser,
        inviteUser,
        clearError
    };

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook to use the context
export const useUsers = () => {
    const context = useContext(UserContext);

    if (context === undefined) {
        throw new Error("useUsers must be used within a UserProvider");
    }

    return context;
};
